import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';

// Gates a route on TWO conditions:
//   1. Vendor flag `multi_school.enabled` is ON for this deployment
//      (delivered via /api/schools/current, cached in AuthContext).
//   2. The caller is the MASTER OWNER — the single account designated
//      by the first-run setup wizard, tracked as `users.is_owner`.
//
// This used to admit anyone with the `super_admin` role. That made
// creating a school an accidental side effect of promoting someone:
// a second school's own administrator, or a regional admin allowed to
// switch schools, silently gained the power to create, rename and
// deactivate schools. Managing schools is an owner-level right, so it
// belongs to one named account and nothing else confers it.
//
// `canManageSchools` comes from /api/schools/current rather than being
// derived from the token, so a hand-over takes effect on the next page
// load instead of when the old token expires. The API re-checks it on
// every call regardless — this guard only hides the screen.
//
// On any failure we route to /dashboard rather than /login so
// existing sessions aren't dropped when they hit the URL by mistake.
export default function MultiSchoolGuard({ children }) {
  const { user, multiSchoolLoading, canManageSchools, canRequestSchool } = useAuth();

  if (!user) return <Navigate to="/login" replace />;
  if (multiSchoolLoading) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // The vendor flag is no longer checked here. Before the module is
  // switched on the owner still needs this page — it is where they
  // REQUEST a school, and an approved request is what switches the
  // module on. `canRequestSchool` is owner-only, so this does not
  // widen access; the page itself shows the request form instead of
  // the management table until the module is live.
  if (!canManageSchools && !canRequestSchool) return <Navigate to="/dashboard" replace />;

  return children;
}
